// =======================================================================================================
// Manager for the global states
// =======================================================================================================
import _ from "lodash";
import { DebugEx } from "./DebugEx";
import { setGlobal } from './Globals';

// =======================================================================================================
export type StateUpdateCB<T> = (state: T, forceUpdate: boolean, changedSubKeys: string[]) => void;
export type StateReducerCB<T, K> = (state: T, actionData: K) => { state: T, isChanged: boolean, changedSubKeys?: string[] };
// -------------------------------------------------------------------------------------------------------
/**
 * T: State structure
 * K: Reducer despatch data 
 * */
export class StateManager<T = any, K = any> {
    // ---------------------------------------------------------------------------------------------------
    private States: { [key: string]: T };
    private StatesUpdateCB: { [key: string]: StateUpdateCB<T>[] };
    private Reducers: { [actionType: string]: StateReducerCB<T, K> };
    // ---------------------------------------------------------------------------------------------------
    constructor(key: string, reducer: { [actionType: string]: StateReducerCB<T, K> }) {
        if (_.has(StateManager._instanceDict, key)) {
            DebugEx.TraceError("Create a state that is already exist", key);
        }

        // Link the dictionary to the global function
        setGlobal("StateManager", StateManager._instanceDict);

        // link to dict
        StateManager._instanceDict[key] = this;
        this.States = {};
        this.StatesUpdateCB = {};
        this.Reducers = reducer;
    }
    // ---------------------------------------------------------------------------------------------------
    public RunMultipleReduce(actionType: string, actions: { key: string, data: K }[]) {
        if (_.has(this.Reducers, actionType)) {
            let changedKeys: { key: string, subKeys: string[] }[] = [];
            // Update states
            for (let action of actions) {
                try {
                    let state = this.States[action.key];
                    let res = this.Reducers[actionType](state, action.data);
                    if (res.isChanged) {
                        this.States[action.key] = res.state;
                        changedKeys.push({ key: action.key, subKeys: res.changedSubKeys });
                    }
                } catch (ex) {
                    DebugEx.TraceException(ex);
                }
            }

            // Run the update cb
            _(changedKeys)
                .groupBy(x => x.key)
                .forEach(x => {
                    let k = x[0].key;
                    let subKeys = x.map(k => k.subKeys).flat();
                    let state = this.States[k];
                    if (state != null && _.has(this.StatesUpdateCB, k))
                        for (let cb of this.StatesUpdateCB[k])
                            cb(state, false, subKeys);
                });
        } else {
            DebugEx.TraceError("Try to run not existing action type");
        }
    }
    // ---------------------------------------------------------------------------------------------------
    public GetState(key: string) {
        return this.States[key];
    }
    // ---------------------------------------------------------------------------------------------------
    /**
     * Caution this is the internal states
     */
    public GetStates() {
        return this.States;
    }
    // ---------------------------------------------------------------------------------------------------
    public RegisterCB(key: string, cb: StateUpdateCB<T>) {
        if (!_.has(this.StatesUpdateCB, key))
            this.StatesUpdateCB[key] = [];
        let cbList = this.StatesUpdateCB[key];
        if (!_.includes(cbList, cb)) {
            cbList.push(cb);
        }
    }
    // ---------------------------------------------------------------------------------------------------
    public UnRegisterCB(key: string, cb: StateUpdateCB<T>) {
        if (_.has(this.StatesUpdateCB, key)) {
            _.pull(this.StatesUpdateCB[key], cb);
        }
    }
    // ---------------------------------------------------------------------------------------------------
    public ForceUpdateAll() {
        // Run the update cb
        for (let k in this.StatesUpdateCB)
            if (_.has(this.StatesUpdateCB, k) &&
                _.has(this.States, k)) {
                let state = this.States[k];
                for (let cb of this.StatesUpdateCB[k])
                    cb(state, true, _.keys(state));
            }
    }
    // ---------------------------------------------------------------------------------------------------
    private static _instanceDict: { [key: string]: StateManager } = {};
    public static GetInstanceKey(nodeKey: string) { return (_.has(this._instanceDict, nodeKey)) ? this._instanceDict[nodeKey] : null; };
    // ---------------------------------------------------------------------------------------------------
}
// =======================================================================================================